/**
 * Local (IndexedDB) mirror of the backend permanent-delete datasource cascade
 * (closot-api BlockService.permanentDelete). Removes the deleted blocks' owned
 * datasource records (mainView === blockId) and marks their row/template pages
 * as status "deleted" — same soft-delete shape as the root; other descendant
 * blocks are left untouched, matching the pre-existing local delete behavior.
 * Writes no syncQueue entries — the server derives the full cascade from the
 * already-queued BLOCK_DELETE op.
 */

import { appDB } from "../db/client";
import { blockStore } from "../store/blockStore";
import type { BlockRecord, DataSourceRecord } from "../db/schema";
import { isIDBAvailable } from "../idbStatus";
import { markDatasourceDead, emitDatasourceCascade } from "@/lib/datasourceTombstones";

export interface CascadeDeleteOptions {
  rootBlockIds: string[];
  workspaceId: string;
}

/**
 * Local cleanup for a datasource the server says is gone (404), which this
 * client never deleted itself — same end state as the cascade: rows marked
 * deleted, the datasource record removed, contexts notified.
 */
export async function purgeDeadDatasourceInIDB(dataSourceId: string): Promise<void> {
  const id = String(dataSourceId || "");
  if (!isIDBAvailable() || !id) return;

  const updatedRows: BlockRecord[] = [];
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.dataSources], async () => {
      const rows = await appDB.blocks.where("parentId").equals(id).toArray();
      const now = Date.now();
      const pending = rows
        .filter((row) => row.status !== "deleted")
        .map((row) => ({ ...row, status: "deleted" as const, localUpdatedAt: now }));
      if (pending.length > 0) {
        await appDB.blocks.bulkPut(pending);
        updatedRows.push(...pending);
      }
      await appDB.dataSources.delete(id);
    });
  } catch (err) {
    console.warn("[purgeDeadDatasourceInIDB] failed:", err);
    return;
  }

  updatedRows.forEach((row) => blockStore.upsert(row));
  emitDatasourceCascade({ dataSourceIds: [id] });
}

/**
 * Best-effort, never throws — a failure here must not trip the caller's
 * direct-API fallback (the delete op is already queued; the server cascade
 * catches up on sync).
 */
export async function cascadeDatasourceDeleteInIDB(
  options: CascadeDeleteOptions,
): Promise<void> {
  const { rootBlockIds, workspaceId } = options;
  if (!isIDBAvailable() || rootBlockIds.length === 0 || !workspaceId) return;

  // Applied only after the transaction commits.
  const storeUpserts: BlockRecord[] = [];
  const cascadedDsIds: string[] = [];

  try {
  await appDB.transaction("rw", [appDB.blocks, appDB.dataSources], async () => {
    // Lazy — most deletes are plain content blocks that never touch a datasource
    let allSources: DataSourceRecord[] | null = null;
    const getAllSources = async (): Promise<DataSourceRecord[]> => {
      allSources ??= await appDB.dataSources
        .where("workspaceId")
        .equals(workspaceId)
        .toArray();
      return allSources;
    };

    const visited = new Set<string>(rootBlockIds);
    const deletedDsIds = new Set<string>();
    const rowIds = new Set<string>();

    // Walks blockIds only to discover nested collection_views; visited blocks
    // are not deleted.
    const expand = async (roots: string[]): Promise<void> => {
      const frontier = [...roots];
      while (frontier.length > 0) {
        const batch = frontier.splice(0, 200);
        const docs = (await appDB.blocks.bulkGet(batch)).filter(
          (b): b is BlockRecord => Boolean(b),
        );
        const collectionViewIds: string[] = [];
        for (const doc of docs) {
          for (const childId of doc.blockIds ?? []) {
            if (!visited.has(childId)) {
              visited.add(childId);
              frontier.push(childId);
            }
          }
          if (doc.blockType === "collection_view") {
            collectionViewIds.push(doc._id);
          }
        }
        if (collectionViewIds.length === 0) continue;
        for (const ds of await getAllSources()) {
          if (deletedDsIds.has(ds._id) || !collectionViewIds.includes(ds.mainView)) {
            continue;
          }
          deletedDsIds.add(ds._id);
          const rowRoots = new Set<string>([
            ...(ds.blockIds ?? []),
            ...(ds.pageTemplateIds ?? []),
          ]);
          const parentRows = await appDB.blocks
            .where("parentId")
            .equals(ds._id)
            .primaryKeys();
          parentRows.forEach((id) => rowRoots.add(String(id)));
          for (const id of rowRoots) {
            rowIds.add(id);
            if (!visited.has(id)) {
              visited.add(id);
              frontier.push(id);
            }
          }
        }
      }
    };

    await expand(rootBlockIds);

    if (rowIds.size > 0) {
      const rows = (await appDB.blocks.bulkGet([...rowIds])).filter(
        (b): b is BlockRecord => Boolean(b) && b!.status !== "deleted",
      );
      const now = Date.now();
      const updated = rows.map((row) => ({
        ...row,
        status: "deleted" as const,
        localUpdatedAt: now,
      }));
      if (updated.length > 0) {
        await appDB.blocks.bulkPut(updated);
        storeUpserts.push(...updated);
      }
    }
    if (deletedDsIds.size > 0) {
      await appDB.dataSources.bulkDelete([...deletedDsIds]);
      cascadedDsIds.push(...deletedDsIds);
    }
  });
  } catch (err) {
    console.warn("[cascadeDatasourceDeleteInIDB] best-effort cascade failed:", err);
    return;
  }

  // useLive* hooks re-query IDB on their own; blockStore consumers need this
  storeUpserts.forEach((block) => blockStore.upsert(block));

  if (cascadedDsIds.length > 0) {
    // This client deleted them, so no 404 will ever arrive to prove it
    cascadedDsIds.forEach((id) => markDatasourceDead(id));
    // React contexts hold their own copy of the datasources and are what the
    // UI renders from — IDB writes alone are invisible to them.
    emitDatasourceCascade({ dataSourceIds: cascadedDsIds });
  }
}
